import { useState, useEffect, useMemo } from "react";
import api, { formatIDR } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import RupiahInput from "@/components/RupiahInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calculator as CalcIcon } from "@phosphor-icons/react";
import { computeSchedulePreview } from "@/lib/schedule";

export default function Calculator() {
  const [cars, setCars] = useState([]);
  const [rates, setRates] = useState([]);
  const [carId, setCarId] = useState("");
  const [price, setPrice] = useState("");
  const [dp, setDp] = useState("0");
  const [tenor, setTenor] = useState("12");
  const [period, setPeriod] = useState("1");
  const [rateId, setRateId] = useState("");

  useEffect(() => {
    api.get("/cars").then((r) => setCars(r.data.filter((c) => c.status === "available")));
    api.get("/interest-rates").then((r) => setRates(r.data));
  }, []);

  const rate = useMemo(() => rates.find((r) => r.id === rateId), [rates, rateId]);
  const annualRate = Number(rate?.annual_rate || 0);

  const preview = useMemo(() => computeSchedulePreview({
    total_price: price,
    down_payment: dp,
    tenor_months: tenor,
    payment_period_months: period,
    annual_rate: annualRate,
  }), [price, dp, tenor, period, annualRate]);

  const setFromCar = (id) => {
    setCarId(id);
    const c = cars.find((x) => x.id === id);
    if (c) setPrice(String(c.price));
  };

  // build schedule dates preview
  const scheduleDates = useMemo(() => {
    const start = new Date();
    const list = [];
    const p = Math.max(parseInt(period) || 1, 1);
    for (let i = 0; i < preview.nPayments; i++) {
      const d = new Date(start);
      d.setMonth(d.getMonth() + p * (i + 1));
      list.push(d);
    }
    return list;
  }, [preview.nPayments, period]);

  return (
    <div>
      <PageHeader
        title="Kalkulator Cicilan"
        subtitle="Simulasi cicilan untuk mobil stok atau harga manual — sebelum transaksi"
      />
      <div className="p-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <CalcIcon size={18} weight="bold" />
            <div className="text-[10px] uppercase tracking-widest text-gray-600 font-medium">Input Simulasi</div>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Pilih Mobil Stok (opsional)</Label>
              <Select value={carId} onValueChange={setFromCar}>
                <SelectTrigger data-testid="calc-car" className="mt-2 rounded-none">
                  <SelectValue placeholder="Pilih mobil untuk auto-isi harga" />
                </SelectTrigger>
                <SelectContent>
                  {cars.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.brand} {c.model} {c.type ? "· " + c.type : ""} ({c.year}) — {formatIDR(c.price)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Harga Jual</Label>
              <RupiahInput value={price} onChange={(e) => setPrice(e.target.value)} data-testid="calc-price" className="mt-2 rounded-none" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Uang Muka (DP)</Label>
              <RupiahInput value={dp} onChange={(e) => setDp(e.target.value)} data-testid="calc-dp" className="mt-2 rounded-none" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Tenor (bulan)</Label>
                <Input type="number" value={tenor} onChange={(e) => setTenor(e.target.value)} data-testid="calc-tenor" className="mt-2 rounded-none font-mono-num" min="1" max="48" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Periode Bayar</Label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger data-testid="calc-period" className="mt-2 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Setiap Bulan</SelectItem>
                    <SelectItem value="4">Setiap 4 Bulan</SelectItem>
                    <SelectItem value="6">Setiap 6 Bulan</SelectItem>
                    <SelectItem value="12">Setiap Tahun</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Bunga (%/tahun)</Label>
              <Select value={rateId} onValueChange={setRateId}>
                <SelectTrigger data-testid="calc-rate" className="mt-2 rounded-none">
                  <SelectValue placeholder="Pilih bunga (default 0%)" />
                </SelectTrigger>
                <SelectContent>
                  {rates.map((r) => (<SelectItem key={r.id} value={r.id}>{r.code} — {r.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div>
          <div className="border border-gray-200 p-6 bg-[#0A0A0A] text-white">
            <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">Angsuran per Bayar</div>
            <div className="font-mono-num text-4xl font-bold" data-testid="calc-per-payment">{formatIDR(preview.perPayment)}</div>
            <div className="text-xs text-white/60 mt-2 font-mono-num">
              {preview.nPayments} kali × setiap {period} bulan
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-gray-200 -m-px mt-4">
            <div className="border border-gray-200 p-5">
              <div className="text-[10px] uppercase tracking-widest text-gray-500">Pokok Pinjaman</div>
              <div className="mt-2 font-mono-num font-semibold">{formatIDR(preview.principal)}</div>
              <div className="text-[11px] text-gray-500 mt-1 font-mono-num">Harga − DP</div>
            </div>
            <div className="border border-gray-200 p-5">
              <div className="text-[10px] uppercase tracking-widest text-gray-500">Total Bunga</div>
              <div className="mt-2 font-mono-num font-semibold">{formatIDR(preview.totalInterest)}</div>
              <div className="text-[11px] text-gray-500 mt-1 font-mono-num">{annualRate}% / thn · flat</div>
            </div>
            <div className="border border-gray-200 p-5">
              <div className="text-[10px] uppercase tracking-widest text-gray-500">Total Angsuran</div>
              <div className="mt-2 font-mono-num font-semibold">{formatIDR(preview.totalRepay)}</div>
            </div>
            <div className="border border-gray-200 p-5 bg-orange-50">
              <div className="text-[10px] uppercase tracking-widest text-gray-500">Total Bayar (DP + Angsuran)</div>
              <div className="mt-2 font-mono-num font-semibold text-[#F97316]" data-testid="calc-grand-total">{formatIDR(preview.grandTotal)}</div>
            </div>
          </div>

          {preview.nPayments > 0 && (
            <div className="mt-6 border border-gray-200">
              <div className="p-4 border-b border-gray-200 text-[10px] uppercase tracking-widest text-gray-600 font-medium">Jadwal Cicilan (Perkiraan)</div>
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {scheduleDates.map((d, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="p-3 font-mono-num text-xs w-12">#{i + 1}</td>
                        <td className="p-3 font-mono-num text-xs text-gray-600">{d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}</td>
                        <td className="p-3 text-right font-mono-num font-semibold">{formatIDR(preview.perPayment)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
